import React from "react";
import { createPortal } from "react-dom";
import { useEscapeKey, useFocusTrap, useLockBodyScroll } from "../../hooks";
import "./VrStage.css";

/**
 * Fullscreen frame for a hosted virtual tour.
 *
 * The previous version of the site inlined this iframe twice — once in the
 * Mars promo and once on the project detail page — with slightly different
 * close buttons and no keyboard escape. This is the single implementation,
 * and it adds the things an overlay needs to be usable:
 *
 *  - rendered through a portal, so no ancestor's `overflow` or `transform`
 *    can clip or mis-position it;
 *  - Escape closes it, and focus is trapped inside while it is open;
 *  - background scrolling is locked;
 *  - `allow="xr-spatial-tracking; fullscreen; gyroscope; accelerometer"` so
 *    the embedded tour can actually enter headset and gyroscope modes.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.tourUrl      URL of the hosted tour.
 * @param {string} props.projectName  Used for the iframe title and heading.
 * @returns {JSX.Element | null}
 */
function VrStage({ isOpen, onClose, tourUrl, projectName }) {
  useLockBodyScroll(isOpen);
  useEscapeKey(isOpen, onClose);
  const stageRef = useFocusTrap(isOpen);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="vr-stage"
      role="dialog"
      aria-modal="true"
      aria-label={`Virtualna šetnja — ${projectName}`}
      ref={stageRef}
      tabIndex={-1}
    >
      <iframe
        className="vr-stage__frame"
        src={tourUrl}
        title={`Virtualna šetnja — ${projectName}`}
        allow="xr-spatial-tracking; fullscreen; gyroscope; accelerometer; magnetometer"
        allowFullScreen
      />

      <div className="vr-stage__bar">
        <p className="vr-stage__label t-label">
          <span className="vr-stage__dot" aria-hidden="true" />
          Virtualna šetnja — {projectName}
        </p>

        <button type="button" className="vr-stage__close" onClick={onClose}>
          <span>Zatvori</span>
          <svg
            className="vr-stage__close-glyph"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="1.25" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}

export default VrStage;
